<template>
  <div class="col-sm-12 stock-adjustment-page">
    <div class="container history-container">
      <!-- Filters -->
      <div class="filter-panel">
        <!-- <div class="filter-panel-header">
          <div>
            <h6>Stock Adjustment History</h6>
            <p>
              Review adjusted items, confirmation status, and action details.
            </p>
          </div>

          <button
            type="button"
            class="btn reset-btn"
            @click="resetFilters"
            :disabled="loading"
          >
            Reset
          </button>
        </div> -->

        <div class="filter-container">
          <div class="filter-item">
            <label>Distributor</label>
            <select
              v-model="selectedDistributor"
              :disabled="isDistributorLocked"
            >
              <option value="" disabled>SELECT Distributor</option>

              <option v-if="!isDistributorUser" :value="ALL">
                All Distributors
              </option>

              <option
                v-for="distributor in distributorList"
                :key="distributor.profile_id"
                :value="String(distributor.profile_id)"
              >
                {{ distributor.name || "No Name Available" }}
              </option>
            </select>
          </div>

          <div class="filter-item">
            <label>From Date</label>
            <input
              type="date"
              v-model="fromDate"
              class="search-input"
              :max="currentDate"
              @change="handleDateChange"
              @blur="validateFromDate"
            />
          </div>

          <div class="filter-item">
            <label>To Date</label>
            <input
              type="date"
              v-model="toDate"
              class="search-input"
              :max="currentDate"
              @change="handleDateChange"
              @blur="validateToDate"
            />
          </div>

          <div class="filter-item">
            <label>Status</label>
            <select v-model="statusFilter">
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div class="filter-item search-filter-item">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search request, item, comment..."
              class="search-input"
              v-model="searchFilter"
            />
          </div>

          <div class="filter-item action-filter-item">
            <label style="visibility: hidden">Search</label>
            <button
              type="button"
              class="btn button-default"
              @click="getHistoryRequests"
              :disabled="loading"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm"
              ></span>
              <span v-else>Search</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Result Summary -->
      <div v-if="searchedOnce && !loading" class="result-toolbar">
        <div class="result-left">
          <span class="result-chip">
            Requests: {{ filteredRequests.length }} / {{ requests.length }}
          </span>

          <span v-if="selectedDistributorName" class="result-chip">
            {{ selectedDistributorName }}
          </span>

          <span class="result-chip">
            Period: {{ fromDate }} to {{ toDate }}
          </span>
        </div>

        <div class="result-right">
          <span class="mini-count"> {{ totalCounts.pending }} Pending </span>
          <span class="mini-count"> {{ totalCounts.approved }} Approved </span>
          <span class="mini-count"> {{ totalCounts.rejected }} Rejected </span>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="loading-spinner mt-3">
        <i class="fa fa-spinner fa-spin"></i> Loading...
      </div>

      <!-- History List -->
      <div v-if="!loading && filteredRequests.length > 0" class="request-list">
        <div
          v-for="request in filteredRequests"
          :key="request.id"
          class="request-card"
        >
          <button
            type="button"
            class="request-header"
            @click="toggleRequest(request.id)"
          >
            <div class="request-left">
              <div class="request-title-line">
                <i
                  class="fa fa-chevron-right collapse-icon"
                  :class="{ expanded: isExpanded(request.id) }"
                ></i>

                <div>
                  <div class="request-title">
                    Request #{{ formatRequestNo(request.id) }}
                  </div>

                  <small class="text-muted">
                    {{ request.created_by_name || request.created_by || "N/A" }}
                    |
                    {{ formatDate(request.created_at) }}
                  </small>
                </div>
              </div>

              <div v-if="request.comment" class="request-comment">
                {{ request.comment }}
              </div>
            </div>

            <div class="request-right">
              <span :class="requestStatusClass(request).class">
                {{ requestStatusClass(request).text }}
              </span>

              <div class="request-counts">
                <span class="mini-count">
                  {{ getRequestCounts(request).pending }} Pending
                </span>
                <span class="mini-count">
                  {{ getRequestCounts(request).approved }} Approved
                </span>
                <span class="mini-count">
                  {{ getRequestCounts(request).rejected }} Rejected
                </span>
              </div>
            </div>
          </button>

          <transition name="fade-slide">
            <div v-if="isExpanded(request.id)" class="request-body">
              <div class="request-info-row">
                <div class="request-info-item">
                  <label>Created By</label>
                  <span>
                    {{ request.created_by_name || request.created_by || "N/A" }}
                  </span>
                </div>

                <div class="request-info-item">
                  <label>Total Items</label>
                  <span>{{ request.details.length }}</span>
                </div>

                <div class="request-info-item">
                  <label>Request Status</label>
                  <span>{{ requestStatusClass(request).text }}</span>
                </div>
              </div>

              <div class="table-responsive table-shell">
                <table class="main-table">
                  <thead>
                    <tr>
                      <th>Item Name</th>
                      <th>Normal Before</th>
                      <th>Normal Adjust</th>
                      <th>Normal After</th>
                      <th>Reason</th>
                      <th>Damaged Before</th>
                      <th>Damaged Adjust</th>
                      <th>Damaged After</th>
                      <th>Damage Reason</th>
                      <th>Status</th>
                      <th>Action Details</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr v-for="item in request.details" :key="item.id">
                      <td class="item-name">
                        {{ item.item_name || `Item #${item.item_id}` }}
                      </td>

                      <td>
                        <span class="qty-text">
                          {{ formatWholeQty(item.theoretical_qty) }}
                        </span>
                      </td>

                      <td>
                        <span class="adjust-text">
                          {{ formatSignedQty(item.adjusted_qty) }}
                        </span>
                      </td>

                      <td>
                        <span class="qty-text">
                          {{ formatWholeQty(item.physical_qty) }}
                        </span>
                      </td>

                      <td class="reason-cell">
                        {{ item.reason_name || "N/A" }}
                      </td>

                      <td>
                        <span class="qty-text">
                          {{ formatWholeQty(item.damaged_theoretical_qty) }}
                        </span>
                      </td>

                      <td>
                        <span class="adjust-text">
                          {{ formatSignedQty(item.damaged_adjusted_qty) }}
                        </span>
                      </td>

                      <td>
                        <span class="qty-text">
                          {{ formatWholeQty(item.damaged_physical_qty) }}
                        </span>
                      </td>

                      <td class="reason-cell">
                        {{ item.damage_reason_name || "N/A" }}
                      </td>

                      <td>
                        <span :class="itemStatusClass(item).class">
                          {{ itemStatusClass(item).text }}
                        </span>
                      </td>

                      <td>
                        <div
                          v-if="isItemFinalized(item)"
                          class="action-details"
                        >
                          <div>
                            {{
                              item.confirmed_by_name ||
                              item.approved_by_name ||
                              item.confirmed_by ||
                              item.approved_by ||
                              "N/A"
                            }}
                          </div>

                          <small class="text-muted">
                            {{
                              formatDate(item.confirmed_at || item.approved_at)
                            }}
                          </small>
                        </div>
                        <span v-else>N/A</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- Initial Hint -->
      <div v-if="!loading && !searchedOnce" class="idle-state">
        <div class="idle-icon">
          <i class="fa fa-history"></i>
        </div>
        <p>Select distributor and date range, then click Search.</p>
      </div>

      <!-- Empty State -->
      <div
        v-if="!loading && searchedOnce && filteredRequests.length === 0"
        class="not-found"
      >
        <img
          src="@/assets/vectors/data_not_found.png"
          alt="No Stock Adjustment History"
          class="error-image"
        />
        <p class="text-center error-message">Results not found!</p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchStockAdjustmentHistoryList,
  fetchDistributors,
} from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  data() {
    const currentDate = new Date().toISOString().split("T")[0];
    const ALL = "__all__";

    return {
      ALL,
      loading: false,
      token: localStorage.getItem("token_set"),
      profile_id: localStorage.getItem("profile_id"),

      currentDate,
      fromDate: currentDate,
      toDate: currentDate,

      distributorList: [],
      selectedDistributor: "",
      isDistributorLocked: false,

      userLabel: "",
      roleKey: "OTHER",
      myProfileId: Number(localStorage.getItem("profile_id") || 0),

      searchFilter: "",
      statusFilter: "all",
      expandedRequestIds: {},
      requests: [],
      searchedOnce: false,
    };
  },

  computed: {
    isDistributorUser() {
      return this.roleKey === "DIS";
    },

    selectedDistributorName() {
      if (!this.selectedDistributor) {
        return "";
      }

      if (this.selectedDistributor === this.ALL) {
        return "All Distributors";
      }

      const distributor = this.distributorList.find(
        (item) => String(item.profile_id) === String(this.selectedDistributor),
      );

      return distributor?.name || "Selected Distributor";
    },

    totalCounts() {
      const counts = {
        pending: 0,
        approved: 0,
        rejected: 0,
      };

      this.filteredRequests.forEach((request) => {
        const requestCounts = this.getRequestCounts(request);

        counts.pending += requestCounts.pending;
        counts.approved += requestCounts.approved;
        counts.rejected += requestCounts.rejected;
      });

      return counts;
    },

    filteredRequests() {
      const search = this.searchFilter.trim().toLowerCase();

      return this.requests
        .filter((request) => {
          const requestStatus = this.getRequestStatus(request).key;

          if (
            this.statusFilter !== "all" &&
            requestStatus !== this.statusFilter
          ) {
            return false;
          }

          if (!search) {
            return true;
          }

          const detailsText = (request.details || [])
            .map((item) =>
              [
                item.item_name,
                item.item_id,
                item.reason_name,
                item.damage_reason_name,
                this.itemStatusClass(item).text,
              ]
                .filter(Boolean)
                .join(" "),
            )
            .join(" ");

          const searchableText = [
            request.id,
            this.formatRequestNo(request.id),
            request.comment,
            request.distributor_name,
            request.created_by_name,
            request.created_at,
            detailsText,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return searchableText.includes(search);
        })
        .sort((a, b) => {
          const dateA = new Date(a.created_at || 0).getTime();
          const dateB = new Date(b.created_at || 0).getTime();

          if (dateA !== dateB) {
            return dateB - dateA;
          }

          return Number(b.id || 0) - Number(a.id || 0);
        });
    },
  },

  async mounted() {
    this.initRoleFromStorage();
    await this.loadDistributorFilter();

    if (this.isDistributorUser) {
      await this.getHistoryRequests();
    }
  },

  methods: {
    initRoleFromStorage() {
      const labelFromStorage =
        localStorage.getItem("user_label") ||
        localStorage.getItem("userLabel") ||
        localStorage.getItem("role") ||
        "";

      this.userLabel = String(labelFromStorage);
      const label = this.userLabel.toUpperCase().trim();

      if (
        label === "DISTRIBUTOR" ||
        label.startsWith("DISTRIBUTOR") ||
        label === "DIS" ||
        label.startsWith("DIS_")
      ) {
        this.roleKey = "DIS";
      } else {
        this.roleKey = "OTHER";
      }
    },

    async loadDistributorFilter() {
      this.loading = true;

      try {
        const response = await fetchDistributors(this.profile_id, this.token);

        let list = [];
        if (response?.data?.status === "success") {
          list = (response?.data?.data || []).map((distributor) => ({
            profile_id: String(distributor.profile_id),
            name: distributor.name || `Distributor #${distributor.profile_id}`,
          }));
        }

        this.distributorList = [];
        this.selectedDistributor = "";
        this.isDistributorLocked = false;

        if (this.isDistributorUser) {
          if (list.length > 0) {
            this.distributorList = list;
            if (list.length === 1) {
              this.selectedDistributor = String(list[0].profile_id);
              this.isDistributorLocked = true;
            } else {
              this.selectedDistributor = this.ALL;
              this.isDistributorLocked = false;
            }
          } else if (this.myProfileId) {
            this.distributorList = [
              {
                profile_id: String(this.myProfileId),
                name: `Distributor #${this.myProfileId}`,
              },
            ];
            this.selectedDistributor = String(this.myProfileId);
            this.isDistributorLocked = true;
          }
          return;
        }

        this.distributorList = list;
        this.selectedDistributor = this.ALL;
      } catch (error) {
        console.error("Error loading distributors:", error);

        this.distributorList = [];
        this.selectedDistributor = "";
        this.isDistributorLocked = false;
      } finally {
        this.loading = false;
      }
    },

    resetFilters() {
      this.searchFilter = "";
      this.statusFilter = "all";
      this.fromDate = this.currentDate;
      this.toDate = this.currentDate;
      this.requests = [];
      this.expandedRequestIds = {};
      this.searchedOnce = false;

      if (this.isDistributorUser && this.myProfileId) {
        this.selectedDistributor = String(this.myProfileId);
        this.isDistributorLocked = true;
      } else {
        this.selectedDistributor = this.ALL;
        this.isDistributorLocked = false;
      }
    },

    isValidISODate(dateStr) {
      if (!dateStr || typeof dateStr !== "string") {
        return false;
      }

      const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);

      if (!match) {
        return false;
      }

      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);

      const date = new Date(year, month - 1, day);

      return (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
      );
    },

    validateFromDate() {
      if (!this.fromDate || !this.isValidISODate(this.fromDate)) {
        this.showErrorToast("Please select valid From Date");
        this.fromDate = "";
        return false;
      }

      if (this.fromDate > this.currentDate) {
        this.showErrorToast("From Date cannot be in the future");
        this.fromDate = "";
        return false;
      }

      return true;
    },

    validateToDate() {
      if (!this.toDate || !this.isValidISODate(this.toDate)) {
        this.showErrorToast("Please select valid To Date");
        this.toDate = "";
        return false;
      }

      if (this.toDate > this.currentDate) {
        this.showErrorToast("To Date cannot be in the future");
        this.toDate = "";
        return false;
      }

      return true;
    },

    handleDateChange() {
      if (this.fromDate && this.toDate && this.toDate < this.fromDate) {
        this.showErrorToast("Invalid date range picked.");
        this.toDate = "";
      }
    },

    validateFiltersForSearch() {
      if (!this.selectedDistributor) {
        this.showErrorToast("Please select Distributor");
        return false;
      }

      if (this.isDistributorUser && this.selectedDistributor === this.ALL) {
        this.showErrorToast("Please select Distributor");
        return false;
      }

      if (!this.validateFromDate()) {
        return false;
      }

      if (!this.validateToDate()) {
        return false;
      }

      if (this.toDate < this.fromDate) {
        this.showErrorToast("Invalid date range picked.");
        return false;
      }

      return true;
    },

    showErrorToast(message) {
      const toast = useToast();

      toast.error(message, {
        position: "top-right",
        timeout: 3000,
      });
    },

    async getHistoryRequests() {
      if (!this.validateFiltersForSearch()) {
        return;
      }

      try {
        this.loading = true;
        this.searchedOnce = true;

        const params = {
          from_date: this.fromDate,
          to_date: this.toDate,
        };

        if (this.selectedDistributor !== this.ALL) {
          params.profile_id = this.selectedDistributor;
        }

        const response = await fetchStockAdjustmentHistoryList(
          this.profile_id,
          this.token,
          params,
        );

        if (response.data?.status === "success") {
          this.requests = (response.data?.data || []).map((request) => ({
            ...request,
            details: request.details || request.items || [],
          }));

          this.expandedRequestIds = {};
          this.autoExpandFirstRequest();
        } else {
          this.requests = [];
        }
      } catch (error) {
        console.error("Error fetching stock adjustment history:", error);

        const message =
          error.response?.data?.message ||
          "Error loading stock adjustment history.";

        this.showErrorToast(message);
        this.requests = [];
      } finally {
        this.loading = false;
      }
    },

    autoExpandFirstRequest() {
      if (this.requests.length > 0) {
        this.expandedRequestIds = {
          ...this.expandedRequestIds,
          [this.requests[0].id]: true,
        };
      }
    },

    toggleRequest(requestId) {
      this.expandedRequestIds = {
        ...this.expandedRequestIds,
        [requestId]: !this.expandedRequestIds[requestId],
      };
    },

    isExpanded(requestId) {
      return !!this.expandedRequestIds[requestId];
    },

    getItemStatus(item) {
      const status = Number(item.status);

      if (status === 1) {
        return {
          key: "approved",
          label: "Approved",
        };
      }

      if (status === 2) {
        return {
          key: "rejected",
          label: "Rejected",
        };
      }

      return {
        key: "pending",
        label: "Pending",
      };
    },

    itemStatusClass(item) {
      const status = this.getItemStatus(item).key;

      const statusMap = {
        pending: {
          text: "Pending",
          class: "badge badge-pending",
        },
        approved: {
          text: "Approved",
          class: "badge badge-approved",
        },
        rejected: {
          text: "Rejected",
          class: "badge badge-rejected",
        },
      };

      return (
        statusMap[status] || {
          text: "Unknown",
          class: "badge badge-secondary",
        }
      );
    },

    isItemFinalized(item) {
      return this.getItemStatus(item).key !== "pending";
    },

    getRequestCounts(request) {
      const details = request.details || [];

      return {
        pending: details.filter(
          (item) => this.getItemStatus(item).key === "pending",
        ).length,
        approved: details.filter(
          (item) => this.getItemStatus(item).key === "approved",
        ).length,
        rejected: details.filter(
          (item) => this.getItemStatus(item).key === "rejected",
        ).length,
      };
    },

    getRequestStatus(request) {
      const counts = this.getRequestCounts(request);
      const total = (request.details || []).length;

      if (total === 0) {
        return {
          key: "pending",
          label: "Pending",
        };
      }

      if (counts.pending > 0) {
        return {
          key: "pending",
          label: "Pending",
        };
      }

      if (counts.approved === total) {
        return {
          key: "approved",
          label: "Approved",
        };
      }

      if (counts.rejected === total) {
        return {
          key: "rejected",
          label: "Rejected",
        };
      }

      return {
        key: "completed",
        label: "Completed",
      };
    },

    requestStatusClass(request) {
      const status = this.getRequestStatus(request).key;

      const statusMap = {
        pending: {
          text: "Pending",
          class: "badge badge-pending",
        },
        approved: {
          text: "Approved",
          class: "badge badge-approved",
        },
        rejected: {
          text: "Rejected",
          class: "badge badge-rejected",
        },
        completed: {
          text: "Completed",
          class: "badge badge-secondary",
        },
      };

      return (
        statusMap[status] || {
          text: "Unknown",
          class: "badge badge-secondary",
        }
      );
    },

    formatRequestNo(value) {
      const numberValue = Number(value);

      if (!Number.isFinite(numberValue)) {
        return "0000";
      }

      return String(Math.trunc(numberValue)).padStart(4, "0");
    },

    formatDate(value) {
      if (!value) {
        return "N/A";
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      };

      const timeOptions = {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      };

      const formattedDate = date
        .toLocaleDateString("en-CA", options)
        .replace(/\//g, "-");

      const formattedTime = date.toLocaleTimeString("en-US", timeOptions);

      return `${formattedDate} | ${formattedTime}`;
    },

    formatWholeQty(value) {
      const numberValue = Number(value);

      if (!Number.isFinite(numberValue)) {
        return 0;
      }

      return Math.trunc(numberValue);
    },

    formatSignedQty(value) {
      const numberValue = Number(value);

      if (!Number.isFinite(numberValue) || numberValue === 0) {
        return 0;
      }

      const wholeValue = Math.trunc(numberValue);

      return wholeValue > 0 ? `+${wholeValue}` : `${wholeValue}`;
    },
  },
};
</script>

<style scoped>
html,
body {
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: #f0f0f0;
}

.stock-adjustment-page {
  padding-bottom: 10px;
}

.history-container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  width: 100%;
  box-sizing: border-box;
  height: auto;
  overflow: visible;
}

.filter-panel {
  border: 1px solid #e7e7e7;
  border-radius: 8px;
  background: #ffffff;
  padding: 14px;
  margin-bottom: 14px;
  flex-shrink: 0;
}

.filter-panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eeeeee;
}

.filter-panel-header h6 {
  margin: 0;
  color: #333333;
  font-size: 15px;
  font-weight: 700;
}

.filter-panel-header p {
  margin: 3px 0 0 0;
  color: #777777;
  font-size: 12px;
}

.filter-container {
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
  width: 100%;
  align-items: end;
  overflow-x: auto;
  padding-bottom: 8px;
}

.filter-item {
  flex: 0 0 auto;
}

.filter-item:nth-child(1) { width: 230px; }
.filter-item:nth-child(2) { width: 170px; }
.filter-item:nth-child(3) { width: 170px; }
.filter-item:nth-child(4) { width: 140px; }

.search-filter-item {
  min-width: 240px;
  flex: 1 1 auto;
}

.action-filter-item {
  width: 120px;
}

.filter-item input,
.filter-item select,
.filter-item button {
  width: 100% !important;
  height: 40px !important;
  box-sizing: border-box;
}

.action-filter-item .button-default {
  height: 40px !important;
  padding: 9px 14px;
  font-size: 13px;
  font-weight: 700;
}

.action-filter-item label {
  margin-bottom: 5px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 13px;
  color: #333333;
}

input[type="text"],
input[type="date"],
select {
  padding: 9px 10px;
  border: 1px solid #cccccc;
  border-radius: 5px;
  background: #ffffff;
  color: #333333;
  font-size: 13px;
  width: 100%;
  height: 40px;
}

input[type="text"]:focus,
input[type="date"]:focus,
select:focus {
  border-color: #888888;
  outline: none;
  box-shadow: 0 0 0 2px rgba(90, 97, 102, 0.08);
}

select:disabled {
  background-color: #f4f4f4;
  cursor: not-allowed;
}

.btn.button-default {
  padding: 10px 18px;
  background-color: #5a6166;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 13px;
  width: 100%;
  height: 40px;
  transition: background-color 0.2s, box-shadow 0.2s;
}

.btn.button-default:hover {
  background-color: #4a5055;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
}

.btn.button-default:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.reset-btn {
  height: 34px;
  min-width: 75px;
  padding: 6px 12px;
  background: #ffffff;
  color: #5a6166;
  border: 1px solid #cccccc;
  border-radius: 5px;
  font-size: 12px;
}

.reset-btn:hover {
  background: #f4f4f4;
}

.result-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  border: 1px solid #eeeeee;
  background: #fafafa;
  border-radius: 7px;
  padding: 10px 12px;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.result-left,
.result-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.result-chip {
  display: inline-block;
  padding: 4px 8px;
  border: 1px solid #dddddd;
  border-radius: 4px;
  background: #ffffff;
  color: #555555;
  font-size: 11px;
  font-weight: 600;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

.request-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-height: 88dvh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
  padding-bottom: 90px;
  margin-bottom: 20px;
  overscroll-behavior: contain;
}

.request-card {
  flex: 0 0 auto;
  border: 1px solid #dddddd;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.request-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  min-height: 96px;
  padding: 16px 14px;
  border: none;
  border-bottom: 1px solid #eeeeee;
  background: #ffffff;
  text-align: left;
  cursor: pointer;
}

.request-header:hover {
  background: #f9f9f9;
}

.request-left {
  min-width: 0;
  flex: 1;
}

.request-title-line {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.collapse-icon {
  color: #5a6166;
  font-size: 12px;
  width: 14px;
  margin-top: 4px;
  transform: rotate(0deg);
  transition: transform 0.22s ease, color 0.18s ease;
}

.collapse-icon.expanded {
  transform: rotate(90deg);
  color: #3f464b;
}

.request-title {
  color: #333333;
  font-size: 14px;
  font-weight: bold;
}

.text-muted {
  color: #6c757d;
  font-size: 0.85em;
}

.request-comment {
  margin-top: 8px;
  margin-left: 24px;
  color: #555555;
  font-size: 13px;
  white-space: normal;
  line-height: 1.35;
  word-break: break-word;
}

.request-right {
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
}

.request-counts {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.mini-count {
  display: inline-block;
  padding: 5px 9px;
  border: 1px solid #dddddd;
  border-radius: 4px;
  background: #ffffff;
  color: #555555;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.request-body {
  padding: 12px;
  background: #ffffff;
}

.request-info-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
  margin-bottom: 12px;
}

.request-info-item {
  flex: 1;
  min-width: 180px;
  padding: 12px;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  background: #fafafa;
}

.request-info-item label {
  margin-bottom: 5px;
  color: #555555;
  font-size: 12px;
}

.request-info-item span {
  color: #333333;
  font-size: 13px;
  font-weight: 600;
}

.table-shell {
  width: 100%;
  max-height: 365px;
  overflow: auto;
  border: 1px solid #eeeeee;
  border-radius: 6px;
}

.table-shell::-webkit-scrollbar-track {
  background: transparent;
}

.table-shell::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(90, 97, 102, 0.38);
}

.table-shell::-webkit-scrollbar-thumb:hover {
  background: rgba(90, 97, 102, 0.6);
}

.main-table {
  width: 100%;
  min-width: 1250px;
  border-collapse: collapse;
  margin: 0;
  border: none;
}

.main-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  text-align: left;
  border: none;
  border-bottom: 2px solid #dddddd;
  background-color: #f4f4f4;
  color: #333333;
  padding: 10px;
  white-space: nowrap;
  font-size: 13px;
}

.main-table td {
  text-align: left;
  border: none;
  padding: 10px;
  white-space: nowrap;
  border-bottom: 1px solid #eeeeee;
  color: #333333;
  font-size: 13px;
  background: #ffffff;
}

.main-table tbody tr:hover td {
  background: #fafafa;
}

.main-table tbody tr:last-child td {
  border-bottom: none;
}

.item-name {
  font-weight: bold;
  color: #333333;
}

.reason-cell {
  max-width: 170px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qty-text,
.adjust-text {
  display: inline-block;
  min-width: 42px;
  padding: 3px 8px;
  border: 1px solid #dddddd;
  border-radius: 3px;
  background: #ffffff;
  color: #333333;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
}

.action-details {
  color: #333333;
}

.badge {
  display: inline-block;
  padding: 5px 10px;
  border-radius: 3px;
  font-size: 12px;
  color: white;
  text-align: center;
  min-width: 75px;
}

.badge-pending {
  background-color: #ff9800;
}

.badge-approved {
  background-color: #28a745;
}

.badge-rejected {
  background-color: #f00;
}

.badge-secondary {
  background-color: #6c757d;
}

.idle-state,
.not-found {
  width: 100%;
  text-align: center;
  padding: 28px 10px;
}

.idle-icon {
  width: 44px;
  height: 44px;
  border: 1px solid #dddddd;
  border-radius: 50%;
  background: #ffffff;
  color: #6c757d;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.idle-state p {
  color: #777777;
  font-size: 13px;
  margin: 0;
}

.error-image {
  max-width: 250px;
}

.error-message {
  font-size: 25px;
  font-weight: bold;
  color: #cccccc;
  margin-top: -25px;
}

.fade-slide-enter-active {
  overflow: hidden;
  transition: max-height 0.28s ease, opacity 0.18s ease, transform 0.22s ease;
}

.fade-slide-leave-active {
  overflow: hidden;
  transition: max-height 0.22s ease, opacity 0.16s ease, transform 0.18s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-4px);
}

.fade-slide-enter-to,
.fade-slide-leave-from {
  max-height: 760px;
  opacity: 1;
  transform: translateY(0);
}

/* Premium tiny scrollbars for every scrollable area in this page */
.stock-adjustment-page,
.stock-adjustment-page * {
  scrollbar-width: thin;
  scrollbar-color: rgba(90, 97, 102, 0.34) transparent;
}

.stock-adjustment-page *::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.stock-adjustment-page *::-webkit-scrollbar-track {
  background: transparent;
}

.stock-adjustment-page *::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(90, 97, 102, 0.28);
}

.stock-adjustment-page *::-webkit-scrollbar-thumb:hover {
  background: rgba(90, 97, 102, 0.55);
}

.stock-adjustment-page *::-webkit-scrollbar-corner {
  background: transparent;
}

@media (max-width: 992px) {
  .filter-panel-header,
  .request-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .request-right {
    align-items: flex-start;
  }

  .request-counts {
    justify-content: flex-start;
  }

  .request-header {
    min-height: auto;
  }
}

@media (max-width: 768px) {
}

@media (max-width: 576px) {
  .stock-adjustment-page {
    padding-bottom: 80px;
  }

  .history-container {
    padding: 12px;
    margin: 6px;
  }

  .request-info-item {
    min-width: 100%;
  }

  .request-comment {
    margin-left: 0;
  }
}
</style>
